/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LINT_UNUSED_FABROOM
 * Date:         2025-12-31
 * Modified By:  Cursor
 *
 * Description:
 * Removed unused faBroom import from fontawesome icons.
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/components/DesktopUsersTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_UI_SPLIT_DESKTOP_REFINED
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refined desktop table view to resolve compilation issues.
 */

import * as React from "react";
import { Table, Badge, Form, Card, Dropdown, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faUserEdit, faUserSlash, faEye, faUserSecret } from "@fortawesome/free-solid-svg-icons"; // [2026-06-23] impersonation: added faUserSecret
import { AdminUser } from "../../../../interfaces/adminUsers";
import { formatUserForDisplay } from "../../../../services/adminUsersService";
import styles from "../../AdminUsers.module.scss";

interface DesktopUsersTableProps {
  data: AdminUser[];
  selected: string[];
  handleSelectAll: (checked: boolean) => void;
  handleRowSelect: (id: string, checked: boolean) => void;
  handleSort: (field: string) => void;
  getSortIcon: (field: string) => any;
  formatDate: (date?: string) => string;
  onEdit: (id: string) => void;
  onDelete?: (id: string) => void;
  onImpersonate?: (id: string) => void;
  onPreviewWorkingData?: (userId: string, userName: string, userEmail?: string) => void;
  selectedUserForCleanup?: string | null;
}

export function DesktopUsersTable({
  data,
  selected,
  handleSelectAll,
  handleRowSelect,
  handleSort,
  getSortIcon,
  formatDate,
  onEdit,
  onDelete,
  onImpersonate,
  onPreviewWorkingData,
  selectedUserForCleanup,
}: DesktopUsersTableProps) {
  return (
    <Card
      className={styles['desktop-users-card']}
      style={{ width: "100%", maxWidth: "100%" }}
    >
      <Card.Body className="p-0">
        <div style={{ width: "100%", overflowX: "auto" }}>
          <Table responsive hover className="mb-0" style={{ width: "100%", minWidth: "1000px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "50px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selected.length === data.length && data.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                {[
                  { key: "firstName", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "isAdmin", label: "Role" },
                  { key: "status", label: "Status" },
                ].map((col) => (
                  <th key={col.key} className="cursor-pointer user-select-none" onClick={() => handleSort(col.key)}>
                    {col.label} <FontAwesomeIcon icon={getSortIcon(col.key)} className="ms-1 text-muted" />
                  </th>
                ))}
                <th>Verified</th>
                <th className="cursor-pointer user-select-none" onClick={() => handleSort("lastLogin")}>
                  Last Login <FontAwesomeIcon icon={getSortIcon("lastLogin")} className="ms-1 text-muted" />
                </th>
                <th className="cursor-pointer user-select-none" onClick={() => handleSort("createdAt")}>
                  Registered <FontAwesomeIcon icon={getSortIcon("createdAt")} className="ms-1 text-muted" />
                </th>
                <th style={{ width: "80px" }} className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((user) => {
                const display = formatUserForDisplay(user);
                const isSelected = selected.includes(user._id);
                const isCleaning = selectedUserForCleanup === user._id;

                return (
                  <tr key={user._id} className={isSelected ? "table-primary" : ""}>
                    <td><Form.Check type="checkbox" checked={isSelected} onChange={(e) => handleRowSelect(user._id, e.target.checked)} /></td>
                    <td>
                      {/* [2026-07-09] impersonation-admin: name links to View as User for ALL targets (admins included, for debugging) */}
                      {onImpersonate ? (
                        <button
                          type="button"
                          className={styles['user-name-link']}
                          onClick={() => onImpersonate(user._id)}
                          title="Act as user"
                        >
                          {display.displayName}
                        </button>
                      ) : (
                        <div className="fw-medium">{display.displayName}</div>
                      )}
                    </td>
                    <td><div className="text-muted">{user.email}</div></td>
                    <td>
                      <Badge
                        bg={user.isAdmin ? "primary" : "secondary"}
                        className={styles['role-badge']}
                      >
                        {display.displayRole}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={user.status === "active" ? "success" : user.status === "suspended" ? "danger" : "warning"}>
                        {display.displayStatus}
                      </Badge>
                    </td>
                    <td><Badge bg={user.email_verified === "Y" ? "success" : "secondary"}>{display.displayVerified}</Badge></td>
                    <td><div className="text-muted small">{display.displayLastLogin}</div></td>
                    <td><div className="text-muted small">{formatDate(user.createdAt)}</div></td>
                    <td className={`text-center ${styles['actions-column']}`}>
                      <Dropdown align="end">
                        <Dropdown.Toggle 
                          variant="link" 
                          id={`dropdown-user-${user._id}`}
                          disabled={isCleaning}
                          className={styles['actions-dropdown-toggle']}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </Dropdown.Toggle>

                        <Dropdown.Menu className={styles['dropdown-menu']}>
                          <Dropdown.Item onClick={() => onEdit(user._id)}>
                            <FontAwesomeIcon icon={faUserEdit} className="me-2" /> Edit User
                          </Dropdown.Item>

                          {/* [2026-07-09] impersonation-admin: View as User available for admin targets too */}
                          {onImpersonate && (
                            <Dropdown.Item onClick={() => onImpersonate(user._id)}>
                              <FontAwesomeIcon icon={faUserSecret} className="me-2" /> View as User
                            </Dropdown.Item>
                          )}

                          {onDelete && (
                            <Dropdown.Item onClick={() => onDelete(user._id)} className="text-danger">
                              <FontAwesomeIcon icon={faUserSlash} className="me-2" /> Delete User
                            </Dropdown.Item>
                          )}

                          <Dropdown.Divider className={styles['dropdown-divider']} />

                          {onPreviewWorkingData && (
                            <Dropdown.Item 
                              onClick={() => onPreviewWorkingData(
                                user._id, 
                                display.displayName, 
                                user.email
                              )}
                              disabled={isCleaning}
                              className="text-warning"
                            >
                              {isCleaning ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faEye} className="me-2" /> Preview Working Data
                                </>
                              )}
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}
